import { useState, useEffect } from 'react';
import { Table } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { useToast } from '../../components/ui/use-toast';
import { Search } from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { db } from '../../lib/firebase';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

interface Recolha {
  id: string; // ID do documento no Firestore
  orderId: string; // ID da ordem
  orderNumber: string;
  buyerName: string;
  buyerAddress: string;
  buyerCity: string;
  buyerPostalCode: string;
  sellerCommercialName: string;
  sellerAddress: string;
  sellerCity: string;
  sellerPostalCode: string;
  sellerPhone: string;
  numCaixas: number;
  pesoTotal: number;
  estado: string;
  dataPedido: string;
  dataSolicitacao: string;
  dataRecolhaPrevista: string;
  numeroRecolha?: string;
}

export default function Recolhas() {
  const [recolhas, setRecolhas] = useState<Recolha[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecolha, setSelectedRecolha] = useState<Recolha | null>(null);
  const [numeroRecolha, setNumeroRecolha] = useState('');
  const [dataRecolhaPrevista, setDataRecolhaPrevista] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleAgendar = (recolha: Recolha) => {
    // Só abre o modal se o documento existir
    if (recolha.id) {
      setSelectedRecolha(recolha);
      setShowModal(true);
      if (recolha.dataRecolhaPrevista) {
        setDataRecolhaPrevista(format(new Date(recolha.dataRecolhaPrevista), "yyyy-MM-dd'T'HH:mm"));
      }
    } else {
      toast({
        title: 'Erro',
        description: 'Não foi possível encontrar o pedido de recolha.',
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async () => {
    if (!selectedRecolha || !numeroRecolha || !dataRecolhaPrevista) {
      toast({
        title: 'Erro',
        description: 'Por favor, preencha todos os campos.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Usar o ID do documento do Firestore
      const recolhaRef = doc(db, 'recolhas', selectedRecolha.id);
      
      const updateData = {
        estado: 'Agendado',
        numeroRecolha: numeroRecolha,
        dataRecolhaPrevista: new Date(dataRecolhaPrevista).toISOString(),
        dataSolicitacao: new Date().toISOString()
      };

      // Atualizar o documento existente
      await updateDoc(recolhaRef, updateData);

      const updatedRecolha = {
        ...selectedRecolha,
        ...updateData
      };

      setRecolhas(prevRecolhas => 
        prevRecolhas.map(r => 
          r.id === selectedRecolha.id ? updatedRecolha : r
        )
      );

      toast({
        title: 'Sucesso',
        description: 'Pedido de recolha agendado com sucesso.',
      });

      setShowModal(false);
      setNumeroRecolha('');
      setDataRecolhaPrevista('');
      setSelectedRecolha(null);
    } catch (error) {
      console.error('Erro ao atualizar recolha:', error);
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao agendar a recolha. Por favor, tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const handleViewOrder = (orderId: string) => {
    navigate(`/dashboard/orders/${orderId}`);
  };

  const getFilteredRecolhas = () => {
    if (!searchTerm) return recolhas;

    return recolhas.filter(recolha =>
      recolha.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recolha.buyerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recolha.sellerCommercialName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  useEffect(() => {
    const fetchRecolhas = async () => {
      try {
        setLoading(true);
        const recolhasRef = collection(db, 'recolhas');
        const recolhasSnapshot = await getDocs(recolhasRef);
        const recolhasData = recolhasSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Recolha[];

        setRecolhas(recolhasData);
      } catch (error) {
        console.error('Erro ao carregar recolhas:', error);
        toast({
          title: 'Erro',
          description: 'Erro ao carregar os pedidos de recolha.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRecolhas();
  }, [toast]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Pedidos de Recolha</h1>
      </div>

      {/* Barra de Pesquisa */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <Input
            type="text"
            placeholder="Pesquisar por número de encomenda, cliente ou vendedor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
          />
        </div>
      </div>

      {/* Tabela de Pedidos */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr className="bg-gray-50">
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Nº Encomenda</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Cliente</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Vendedor</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Nº Caixas</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Peso Total</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Estado</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Data Pedido</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Data Prevista</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Nº Recolha</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-900">Ações</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredRecolhas().map((recolha) => (
                <tr key={recolha.id} className="border-t border-gray-200">
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                    <button 
                      onClick={() => handleViewOrder(recolha.orderId)}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {recolha.orderNumber}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{recolha.buyerName}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{recolha.sellerCommercialName}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{recolha.numCaixas}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{recolha.pesoTotal} kg</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      recolha.estado === 'Agendado' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {recolha.estado}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                    {format(new Date(recolha.dataPedido), 'dd/MM/yyyy HH:mm', { locale: pt })}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                    {recolha.dataRecolhaPrevista ? 
                      format(new Date(recolha.dataRecolhaPrevista), 'dd/MM/yyyy HH:mm', { locale: pt }) : 
                      'Aguardar'
                    }
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{recolha.numeroRecolha || '-'}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                    <Button
                      onClick={() => handleAgendar(recolha)}
                      disabled={recolha.estado === 'Agendado'}
                      variant="outline"
                      size="sm"
                    >
                      Agendar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Modal de Agendamento */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 bg-white rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Agendar Recolha</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Número da Recolha</label>
                <Input
                  value={numeroRecolha}
                  onChange={(e) => setNumeroRecolha(e.target.value)}
                  placeholder="Insira o número da recolha"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Data Prevista de Recolha</label>
                <Input
                  type="datetime-local"
                  value={dataRecolhaPrevista}
                  onChange={(e) => setDataRecolhaPrevista(e.target.value)}
                />
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSubmit}>
                  Confirmar
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
